import type { FieldErrors } from 'react-hook-form';
import { REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';

export { REQUIRED_FIELDS_MESSAGE };

/** Flattens RHF errors into ordered dotted paths (e.g. `activities.0.activityId`). */
export function collectInvalidFieldNames(errors: FieldErrors, prefix = ''): string[] {
  const names: string[] = [];
  for (const key of Object.keys(errors)) {
    const value = (errors as Record<string, unknown>)[key];
    if (value === undefined || value === null || value === false) continue;
    const path = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      const root = (value as { root?: { message?: unknown } }).root;
      if (root && typeof root.message === 'string') names.push(path);
      for (let i = 0; i < value.length; i++) {
        const item = value[i];
        if (item && typeof item === 'object') {
          names.push(...collectInvalidFieldNames(item as FieldErrors, `${path}.${i}`));
        }
      }
    } else if (typeof value === 'object') {
      const message = (value as { message?: unknown }).message;
      if (typeof message === 'string') names.push(path);
      else names.push(...collectInvalidFieldNames(value as FieldErrors, path));
    }
  }
  return names;
}

/** Resolves a field name to its DOM element: native `name` or Radix trigger `data-field-name`. */
export function getFieldElement(name: string): HTMLElement | null {
  const escaped = name.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const byName = document.querySelector<HTMLElement>(`[name="${escaped}"]`);
  if (byName) return byName;
  return document.querySelector<HTMLElement>(`[data-field-name="${escaped}"]`);
}

function scrollAndFocus(el: HTMLElement) {
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (typeof el.focus === 'function') el.focus({ preventScroll: true });
}

/**
 * Finds the first invalid field in visual order, reveals its section/tab (via
 * `revealField`), scrolls it into view and focuses it. Returns true when a
 * first invalid field exists (focus may complete after a reveal).
 */
export function focusFirstInvalidField(
  errors: FieldErrors,
  revealField?: (fieldName: string) => boolean | void
): boolean {
  const names = collectInvalidFieldNames(errors);
  if (names.length === 0) return false;
  const first = names[0];
  const deferred = revealField ? revealField(first) : false;
  if (deferred) {
    window.setTimeout(() => {
      const el = getFieldElement(first);
      if (el) scrollAndFocus(el);
    }, 0);
    return true;
  }
  const el = getFieldElement(first);
  if (el) scrollAndFocus(el);
  return true;
}

/** Maps backend `fields` object to RHF field errors so inline errors render. */
export function applyBackendFieldErrors(
  fields: Record<string, string> | undefined,
  setError: (name: string, error: { type: string; message: string }) => void
): string | undefined {
  if (!fields) return undefined;
  const entries = Object.entries(fields);
  if (entries.length === 0) return undefined;
  for (const [path, message] of entries) {
    setError(path, { type: 'server', message });
  }
  return entries[0][0];
}

/** Focuses the first backend-invalid field so the user sees where to fix it. */
export function focusFirstBackendField(fields: Record<string, string> | undefined): boolean {
  if (!fields) return false;
  const first = Object.keys(fields)[0];
  if (!first) return false;
  const el = getFieldElement(first);
  if (!el) return false;
  scrollAndFocus(el);
  return true;
}

/**
 * Shared submit-error handler. Backend field errors map back to inline errors
 * and focus the first invalid field; otherwise a single safe toast is shown.
 */
export function handleApiSubmitError(
  err: unknown,
  opts: {
    addToast: (message: string, type: 'error' | 'success' | 'info' | 'warning') => void;
    setError?: (name: string, error: { type: string; message: string }) => void;
  }
) {
  const fields = (err as { fields?: Record<string, string> } | null)?.fields;
  const message = (err as { message?: unknown } | null)?.message;
  if (fields && Object.keys(fields).length > 0) {
    const values = Object.values(fields).filter((v) => typeof v === 'string' && v.length > 0);
    if (opts.setError) {
      applyBackendFieldErrors(fields, opts.setError);
      focusFirstBackendField(fields);
    }
    opts.addToast(
      values.length === 1 ? values[0] : REQUIRED_FIELDS_MESSAGE,
      'error'
    );
    return;
  }
  opts.addToast(typeof message === 'string' && message ? message : 'Something went wrong. Please try again.', 'error');
}
